import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  addDays,
  format,
  isSameDay,
  isToday,
  isBefore,
  startOfWeek,
} from "date-fns";

const noop = () => {};

const MiniCalendar = ({
  availableDates,
  selectedDate,
  setSelectedDate,
  setSelectedHour,
  locale,
  onShowMonthly = noop,
}) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isMobile = useMediaQuery("(min-width:600px)");

  const validAvailableDates = useMemo(
    () =>
      availableDates
        ?.map((d) => {
          if (!d?.date) return null;
          const [m, day, y] = d.date.split(" ")[0].split("/");
          const dt = new Date(y, m - 1, day);
          dt.setHours(0, 0, 0, 0);
          return isNaN(dt) ? null : dt;
        })
        .filter((d) => d && !isBefore(d, today)),
    [availableDates, today],
  );

  const weekStart = startOfWeek(today, { locale });
  const twoWeekEnd = addDays(weekStart, 13);
  const hasTwoWeekAvailability = validAvailableDates.some(
    (d) => d >= weekStart && d <= twoWeekEnd,
  );

  useEffect(() => {
    if (!hasTwoWeekAvailability) {
      onShowMonthly();
    }
  }, [hasTwoWeekAvailability, onShowMonthly]);

  const days = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const d = addDays(weekStart, i);
        d.setHours(0, 0, 0, 0);
        return d;
      }),
    [weekStart],
  );

  const weekDays = Array.from({ length: 7 }).map((_, i) =>
    format(addDays(weekStart, i), "EEE", { locale }).toUpperCase(),
  );

  const handleClick = (date) => {
    const isAvailable = validAvailableDates.some((d) => isSameDay(d, date));
    if (isAvailable) setSelectedDate(date);
    setSelectedHour(null);
  };

  return (
    <Box>
      <Box
        display="grid"
        gridTemplateColumns="repeat(7, 44px)"
        gap={isMobile ? 1 : 0.4}
        mb={1}
      >
        {weekDays.map((d) => (
          <Box key={d} textAlign="center">
            <Typography fontSize={12} color="#666">
              {d}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(7, 44px)"
        gap={isMobile ? 1 : 0.4}
      >
        {days.map((date) => {
          const isAvailable = validAvailableDates.some((d) =>
            isSameDay(d, date),
          );
          const selected = selectedDate && isSameDay(selectedDate, date);

          return (
            <Box
              key={date.toISOString()}
              onClick={() => handleClick(date)}
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1,
                bgcolor: isAvailable ? "#f2f2f2" : "",
                cursor: isAvailable ? "pointer" : "not-allowed",
                color: isAvailable ? "inherit" : "#ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                boxShadow: selected ? "0 0px 5px #76bc21" : "none",
                border: selected ? "2px solid #76bc21 !important" : "none",
              }}
            >
              <Typography fontSize={14}>{format(date, "d")}</Typography>

              {isToday(date) && (
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    bgcolor: "#1c355e",
                    borderRadius: "50%",
                    position: "absolute",
                    bottom: 4,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

MiniCalendar.propTypes = {
  availableDates: PropTypes.array.isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  setSelectedDate: PropTypes.func.isRequired,
  locale: PropTypes.object.isRequired,
  onShowMonthly: PropTypes.func,
  setSelectedHour: PropTypes.func,
};

export default MiniCalendar;
